import { useCallback } from 'react';

import {
  useTableState,
  useTableDispatch,
} from '../context/TableSchemaBuilderContext';
import { parseCellId } from '../utils/cellHelpers';

export const useTableModification = () => {
  const { schema } = useTableState();
  const dispatch = useTableDispatch();

  const handleAddColumnBefore = useCallback(
    (beforeIndex) => {
      const newCells = {};
      Object.entries(schema.cells).forEach(([cellId, cellData]) => {
        const [col, row] = parseCellId(cellId);
        if (col >= beforeIndex) {
          const newCol = String.fromCharCode(65 + col);
          newCells[`${newCol}${row}`] = cellData;
        } else {
          newCells[cellId] = cellData;
        }
      });

      // Update column settings
      const newColumnSettings = { ...schema.columnSettings };
      for (let i = schema.columns; i >= beforeIndex; i -= 1) {
        const currentCol = String.fromCharCode(64 + i);
        const nextCol = String.fromCharCode(64 + i + 1);
        newColumnSettings[nextCol] = newColumnSettings[currentCol];
      }
      newColumnSettings[String.fromCharCode(64 + beforeIndex)] = { width: 100 };

      dispatch({
        type: 'UPDATE_SCHEMA',
        payload: {
          ...schema,
          columns: schema.columns + 1,
          cells: newCells,
          columnSettings: newColumnSettings,
        },
      });
    },
    [schema, dispatch]
  );

  const handleAddRowBefore = useCallback(
    (beforeIndex) => {
      const newCells = {};
      Object.entries(schema.cells).forEach(([cellId, cellData]) => {
        const [col, row] = parseCellId(cellId);
        if (row >= beforeIndex) {
          newCells[`${String.fromCharCode(64 + col)}${row + 1}`] = cellData;
        } else {
          newCells[cellId] = cellData;
        }
      });

      // Update row settings
      const newRowSettings = { ...schema.rowSettings };
      for (let i = schema.rows; i >= beforeIndex; i -= 1) {
        newRowSettings[i + 1] = newRowSettings[i];
      }
      newRowSettings[beforeIndex] = { height: 25 };

      dispatch({
        type: 'UPDATE_SCHEMA',
        payload: {
          ...schema,
          rows: schema.rows + 1,
          cells: newCells,
          rowSettings: newRowSettings,
        },
      });
    },
    [schema, dispatch]
  );

  const handleAddColumn = useCallback(
    (afterIndex) => {
      const newCells = {};
      Object.entries(schema.cells).forEach(([cellId, cellData]) => {
        const [col, row] = parseCellId(cellId);
        if (col <= afterIndex) {
          newCells[cellId] = cellData;
        } else {
          const newCol = String.fromCharCode(65 + col);
          newCells[`${newCol}${row}`] = cellData;
        }
      });

      // Update column settings
      const newColumnSettings = { ...schema.columnSettings };
      for (let i = schema.columns; i > afterIndex; i -= 1) {
        const currentCol = String.fromCharCode(64 + i);
        const nextCol = String.fromCharCode(64 + i + 1);
        newColumnSettings[nextCol] = newColumnSettings[currentCol];
      }
      newColumnSettings[String.fromCharCode(64 + afterIndex + 1)] = {
        width: 100,
      };

      dispatch({
        type: 'UPDATE_SCHEMA',
        payload: {
          ...schema,
          columns: schema.columns + 1,
          cells: newCells,
          columnSettings: newColumnSettings,
        },
      });
    },
    [schema, dispatch]
  );

  const handleColumnResize = useCallback(
    (columnName, width) => {
      dispatch({
        type: 'UPDATE_COLUMN_WIDTH',
        payload: {
          columnName,
          width,
        },
      });
    },
    [dispatch]
  );

  const handleAddRow = useCallback(
    (afterIndex) => {
      const newCells = {};
      Object.entries(schema?.cells ?? {}).forEach(([cellId, cellData]) => {
        const [col, row] = parseCellId(cellId);
        if (row <= afterIndex) {
          newCells[cellId] = cellData;
        } else {
          newCells[`${String.fromCharCode(64 + col)}${row + 1}`] = cellData;
        }
      });

      const newRowSettings = { ...schema?.rowSettings };
      for (let i = schema?.rows ?? 0; i > afterIndex; i -= 1) {
        newRowSettings[i + 1] = newRowSettings?.[i];
      }
      newRowSettings[afterIndex + 1] = { height: 25 };

      dispatch({
        type: 'UPDATE_SCHEMA',
        payload: {
          ...schema,
          rows: (schema?.rows ?? 0) + 1,
          cells: newCells,
          rowSettings: newRowSettings,
        },
      });
    },
    [schema, dispatch]
  );

  const handleRowResize = useCallback(
    (rowIndex, height) => {
      dispatch({
        type: 'UPDATE_ROW_HEIGHT',
        payload: {
          rowIndex,
          height,
        },
      });
    },
    [dispatch]
  );

  return {
    handleAddRowBefore,
    handleAddColumnBefore,
    handleAddColumn,
    handleColumnResize,
    handleAddRow,
    handleRowResize,
  };
};
